import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as xtpXdpConnectionSelectors from 'data/xtp-xdp-connection/xtp-xdp-connection.selectors';
import * as xtpXdpConnectionActions from 'data/xtp-xdp-connection/xtp-xdp-connection.actions';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import { XtpXdpConnection, newXtp, newXtpXdpConnectionProtocol } from 'data/xtp-xdp-connection/xtp-xdp-connection.types';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import XtpXdpConnectionForm from '../../components/XtpXdpConnectionForm';
import { Ref } from '../../components/XtpXdpConnectionForm/XtpXdpConnectionForm';
import { xtpXdpConnectionTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFormData,
  adaptXtpXdpConnectionTemplate,
  getNextIdId,
} from '../../utils/xtpXdpConnectionTemplate.adapter';
import {
  getAuditDetailsToSubmit,
  getCFData,
  getProtocolAuditDetailsToSubmit,
  getProtocolsDataToSubmit,
} from '../../utils/xtpXdpConnectionDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  xdpTemplateDef: adaptTemplateVisibilityMode(
    xtpXdpConnectionTemplate,
    'edit',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  xdps: xdpSelectors.getXdps(state),
  xtps: xtpSelectors.getXtps(state),
  xtpXdpConnection: xtpXdpConnectionSelectors.getXtpXdpsConnection(state),
  selectedXtpXdpConnection: xtpXdpConnectionSelectors.getSelectedXtpXdpsConnection(state)
});

const mapDispatch = {
  updateXtpXdpConnection: xtpXdpConnectionActions.updateXtpXdpConnection,
  goToXdpList: xtpXdpConnectionActions.goToXtpXdpConnectionList,
  getXtpXdpConnectionById: xtpXdpConnectionActions.getXtpXdpConnectionById,
  getXdps: xdpActions.getXdps,
  getXtps: xtpActions.getXtps,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const XtpXdpConnectionEdit = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  xdpTemplateDef,
  xdps,
  xtps,
  xtpXdpConnection,
  selectedXtpXdpConnection,
  updateXtpXdpConnection,
  goToXdpList,
  getXtps,
  getXdps,
  getXtpXdpConnectionById,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [ifId, setIfId] = useState(0);
  const [formData, setFormData] = useState<XtpXdpConnection>(
    cloneDeep(newXtp),
  );
  const [template, setTemplate] = useState(xdpTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getXdps(deliveryFunctionId);
    getXtps(deliveryFunctionId);
    const id = selectedXtpXdpConnection?.[0]?.cfid;

    if (id) {
      getXtpXdpConnectionById(deliveryFunctionId, window.btoa(id));
    }
  }, [
    deliveryFunctionId,
    selectedXtpXdpConnection,
    getXtpXdpConnectionById,
    getXdps,
    getXtps
  ]);

  useEffect(() => {
    if (!xtpXdpConnection?.id) {
      return;
    }

    setFormData({
      ...xtpXdpConnection
    });

    const newTemplateData = adaptXtpXdpConnectionTemplate(
      xdpTemplateDef as FormTemplate,
      xdps,
      xtps,
      xtpXdpConnection,
    );

    newTemplateData.key = Date.now().toString()
    newTemplateData.formId = Date.now().toString()

    setTemplate(newTemplateData);
    setIfId(getNextIdId(xtpXdpConnection));
  }, [xdpTemplateDef, xdps, xtps, xtpXdpConnection]);

  const handleFieldUpdate = (
    { name: fieldName, value }: FieldUpdateHandlerParam,
    fieldSection?: 'main' | 'protocol' | 'protocolInterface',
    protolType?: string,
  ) => {
    if (template == null || formData == null) {
      return;
    }

    if (fieldSection === 'protocol') {
      handleProcolSectionFieldUpdate(fieldName, value, formData, template);
    } else if (fieldSection === 'protocolInterface') {
      handleProcolInterfaceSectionFieldUpdate(fieldName, value, formData, template, protolType);
    } else {
      handleMainSectionFieldUpdate(fieldName, value, formData, template);
    }
  };

  const handleMainSectionFieldUpdate = (
    fieldName: string,
    value: string,
    form: XtpXdpConnection,
    formTemplate: FormTemplate,
  ) => {
    const clonedFormData = cloneDeep(form);
    const clonedTemplate = cloneDeep(formTemplate);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  }

  const handleProcolSectionFieldUpdate = (
    fieldName: string,
    value: string | boolean,
    form: XtpXdpConnection,
    formTemplate: FormTemplate,
  ) => {
    const clonedFormData = cloneDeep(form);
    const clonedTemplate = cloneDeep(formTemplate);

    const type = fieldName.toLocaleUpperCase();
    const protocolData = clonedFormData.protocols[type];

    if (protocolData?.id) {
      const newProtocolData = (value === true && protocolData?.operation === 'DELETE') ?
        {
          ...protocolData,
          ...newXtpXdpConnectionProtocol
        } :
        protocolData;

      clonedFormData.protocols[type] = {
        ...newProtocolData,
        operation: value === true ? 'UPDATE' : 'DELETE',
        type,
      }
    } else {
      const newIfId = ifId + 1;

      clonedFormData.protocols[type] = {
        ...newXtpXdpConnectionProtocol,
        operation: value === true ? 'CREATE' : 'DELETE',
        ifid: newIfId.toString(),
        type,
      }

      setIfId(newIfId);
    }

    setFormData(clonedFormData);
    setTemplate(clonedTemplate);
  }

  const handleProcolInterfaceSectionFieldUpdate = (
    fieldName: string,
    value: boolean,
    form: XtpXdpConnection,
    formTemplate: FormTemplate,
    protocolType?: string,
  ) => {
    if (!protocolType) {
      return;
    }
    const clonedFormData = cloneDeep(form);
    const clonedTemplate = cloneDeep(formTemplate);
    const protocolData = clonedFormData.protocols[protocolType];
    if (protocolData == null) {
      return;
    }

    setFormData((current) => {
      return {
        ...current,
        protocols: {
          ...current.protocols,
          [protocolType]: {
            ...protocolData,
            [fieldName]: value
          }
        }
      }
    });

    setTemplate(clonedTemplate);
  }

  const getXtpXdpConnectionActionPayload = () => {

    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const xtpXdpFormData = formRef.current.getValues();

    const connectionPayload = {
      data: xtpXdpFormData.main,
      auditDetails: getAuditDetailsToSubmit(
        xtpXdpFormData.main,
        null,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    const protocolsPayload = getProtocolsDataToSubmit(xtpXdpFormData, formData.protocols)
      .map((item) => ({
        data: item,
        auditDetails: getProtocolAuditDetailsToSubmit(
          item,
          null,
          isAuditEnabled,
          userId,
          userName,
          'UPDATE',
        ),
      }))

    return {
      main: connectionPayload,
      protocols: protocolsPayload,
    };
  };

  const handleClickSave = () => {
    const payload = getXtpXdpConnectionActionPayload();
    console.log(payload)
    if (payload) {
      updateXtpXdpConnection(deliveryFunctionId, payload.main, payload.protocols, history);
    }
  };

  const handleCancel = () => goToXdpList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <XtpXdpConnectionForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (xtpXdpConnection == null) {
    return <div />;
  }

  return (
    <div data-testid="XtpXdpConnectionEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('xtpXdpConnection.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(XtpXdpConnectionEdit));
